import { useMemo, useCallback } from 'react';
import { useAppStore } from '../store';
import { getCategoryDescendants } from '../services/categoryService';
import type { Transaction } from '../types';

export interface TransactionFilters {
  month?: string;
  categoryId?: string;
  cardType?: 'digital' | 'titular' | 'adicional';
  search?: string;
}

export function useTransactions(filters?: TransactionFilters) {
  const transactions = useAppStore((state) => state.transactions);
  const categories = useAppStore((state) => state.categories);

  const filteredTransactions = useMemo(() => {
    if (!filters) return transactions;

    // Build set of category IDs to match (including descendants for parent categories)
    let categoryIds: Set<string> | null = null;
    if (filters.categoryId) {
      const descendants = getCategoryDescendants(
        categories,
        filters.categoryId
      );
      categoryIds = new Set([
        filters.categoryId,
        ...descendants.map((d) => d.id),
      ]);
    }

    return transactions.filter((transaction) => {
      if (filters.month) {
        const transactionMonth = transaction.date.slice(0, 7);
        if (transactionMonth !== filters.month) return false;
      }

      if (categoryIds) {
        if (!transaction.categoryId || !categoryIds.has(transaction.categoryId))
          return false;
      }

      if (filters.cardType) {
        if (transaction.cardType !== filters.cardType) return false;
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (!transaction.description.toLowerCase().includes(searchLower))
          return false;
      }

      return true;
    });
  }, [transactions, categories, filters]);

  return filteredTransactions;
}

export function useTransaction(id: string) {
  const transactions = useAppStore((state) => state.transactions);
  return useMemo(
    () => transactions.find((t) => t.id === id),
    [transactions, id]
  );
}

export function useAddTransactions() {
  const addTransactions = useAppStore((state) => state.addTransactions);
  return useCallback(
    (transactions: Transaction[]) => {
      addTransactions(transactions);
    },
    [addTransactions]
  );
}

export function useUpdateTransaction() {
  const updateTransaction = useAppStore((state) => state.updateTransaction);
  return useCallback(
    (id: string, updates: Partial<Transaction>) => {
      updateTransaction(id, updates);
    },
    [updateTransaction]
  );
}

export function useDeleteTransaction() {
  const deleteTransaction = useAppStore((state) => state.deleteTransaction);
  return useCallback(
    (id: string) => {
      deleteTransaction(id);
    },
    [deleteTransaction]
  );
}
